import { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios, { API } from '@/utils/api';
import DOMPurify from 'dompurify';
import { useAuth } from '@/context/AuthContext';

// Configure DOMPurify to allow iframes from trusted video sources only
const sanitizeConfig = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'loading'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?:)?\/\/(?:www\.)?(?:youtube\.com|youtube-nocookie\.com|youtu\.be|vimeo\.com|player\.vimeo\.com|wistia\.com|fast\.wistia\.net|dailymotion\.com|loom\.com))/i,
};

/**
 * Parse video URL to get embed info
 */
const parseVideoUrl = (videoData) => {
  if (!videoData?.url) return null;
  
  const url = videoData.url.trim();
  const platform = videoData.platform || 'youtube';
  
  // Check if it's already an embed URL or iframe code
  if (url.includes('<iframe')) {
    const srcMatch = url.match(/src=["']([^"']+)["']/);
    if (srcMatch) {
      return { embedUrl: srcMatch[1], isIframe: false };
    }
    return { rawHtml: url, isIframe: true };
  }
  
  // Parse YouTube URLs
  if (platform === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
    let videoId = null;
    
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split(/[?&#]/)[0];
    } else if (url.includes('v=')) {
      videoId = url.split('v=')[1]?.split(/[&#]/)[0];
    } else if (url.includes('/embed/')) {
      videoId = url.split('/embed/')[1]?.split(/[?&#]/)[0];
    } else if (url.includes('/shorts/')) {
      videoId = url.split('/shorts/')[1]?.split(/[?&#]/)[0];
    }
    
    if (videoId) {
      return {
        embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`,
        platform: 'youtube',
      };
    }
  }
  
  // Parse Vimeo URLs
  if (platform === 'vimeo' || url.includes('vimeo.com')) {
    let videoId = null;
    let privacyHash = null;
    
    const vimeoPrivateMatch = url.match(/vimeo\.com\/(\d+)\/([a-zA-Z0-9]+)/);
    if (vimeoPrivateMatch) {
      videoId = vimeoPrivateMatch[1];
      privacyHash = vimeoPrivateMatch[2];
    } else {
      const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) {
        videoId = vimeoMatch[1];
      }
    }
    
    if (!videoId && url.includes('player.vimeo.com/video/')) {
      videoId = url.split('player.vimeo.com/video/')[1]?.split(/[?&#]/)[0];
    }
    
    if (videoId) {
      const embedUrl = privacyHash
        ? `https://player.vimeo.com/video/${videoId}?h=${privacyHash}&autoplay=0`
        : `https://player.vimeo.com/video/${videoId}?autoplay=0`;
      return { embedUrl, platform: 'vimeo' };
    }
  }
  
  if (url.includes('embed') || url.includes('player')) {
    return { embedUrl: url };
  }
  
  return null;
};

/**
 * Single Video Embed Card
 */
const VideoEmbedCard = ({ videoData, onDismiss }) => {
  const embedInfo = useMemo(() => parseVideoUrl(videoData), [videoData]);
  
  // SECURITY: Auth via httpOnly cookies
  const trackClick = async () => {
    try {
      await axios.post(`${API}/video-embed/track`, {
        event_type: 'click',
        video_url: videoData?.url,
        cta_link: videoData?.cta_link,
      });
    } catch (err) {
      console.debug('Video CTA tracking failed:', err);
    }
  };

  const handleCTAClick = () => {
    trackClick();
    if (videoData?.cta_link) {
      window.open(videoData.cta_link, '_blank');
    }
  };
  
  if (!embedInfo) return null;
  
  return (
    <div 
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: 'var(--theme-background-secondary)',
        border: '1px solid var(--theme-border)',
      }}
      data-testid={`video-embed-${videoData.id || videoData.embed_id}`}
    >
      {onDismiss && (
        <button
          onClick={() => onDismiss(videoData.id || videoData.embed_id)}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
        >
          <X className="w-4 h-4 text-white" />
        </button>
      )}
      
      {videoData?.title && (
        <div className="px-4 pt-4 pb-2 text-center">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>
            {videoData.title}
          </h3>
        </div>
      )}
      
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        {embedInfo.isIframe ? (
          <div className="absolute inset-0" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(embedInfo.rawHtml, sanitizeConfig) }} />
        ) : (
          <iframe
            src={embedInfo.embedUrl}
            className="absolute inset-0 w-full h-full"
            title={videoData?.title || 'Embedded video'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
          />
        )}
      </div>
      
      {videoData?.cta_text && (
        <div className="px-4 py-4 flex justify-center">
          <Button
            onClick={handleCTAClick}
            className="px-8 py-3 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
            style={{
              background: videoData.cta_color || 'linear-gradient(135deg, #df1283 0%, #FF7901 100%)',
              color: 'white',
            }}
            data-testid={`video-cta-btn-${videoData.id || videoData.embed_id}`}
          >
            {videoData.cta_text}
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * MultiVideoEmbedSection - Displays multiple YouTube or Vimeo embeds
 * Fetches videos from the appropriate API based on user's type:
 * - B2B users: /api/client-video-embeds/active (their client's videos)
 * - Regular users: /api/video-embeds (admin's videos)
 */
const MultiVideoEmbedSection = ({ onDismiss }) => {
  const { user, loading: authLoading } = useAuth();
  const [videos, setVideos] = useState([]);
  const [dismissedVideos, setDismissedVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      // Wait for auth to be ready
      if (authLoading) return;
      
      try {
        // Use user from AuthContext instead of making a separate /auth/me call
        // B2B users get their client's videos, regular users get admin videos
        const endpoint = user?.client_id 
          ? `${API}/client-video-embeds/active`
          : `${API}/video-embeds`;
        
        const res = await axios.get(endpoint, { withCredentials: true });
        setVideos(res.data.video_embeds || []);
      } catch (error) {
        // Silently fail - video embeds are non-critical
        console.debug('Video embeds fetch failed:', error.message);
      } finally {
        setLoading(false);
      }
    };
    
    // Defer video embeds - non-critical
    const timer = setTimeout(() => {
      fetchVideos();
    }, 1200);
    
    return () => clearTimeout(timer);
  }, [authLoading, user?.client_id]);

  const handleDismiss = (videoId) => {
    setDismissedVideos(prev => [...prev, videoId]);
    if (onDismiss) onDismiss(videoId);
  };

  const visibleVideos = videos.filter(v => !dismissedVideos.includes(v.id || v.embed_id));

  if (loading || visibleVideos.length === 0) return null;

  return (
    <div className="space-y-4 mb-6" data-testid="multi-video-embed-section">
      {visibleVideos.map((video) => (
        <VideoEmbedCard 
          key={video.id || video.embed_id} 
          videoData={video} 
          onDismiss={handleDismiss}
        />
      ))}
    </div>
  );
};

export default MultiVideoEmbedSection;
export { VideoEmbedCard, parseVideoUrl };
